/**
 * Centralized Logging Utility
 *
 * Provides structured logging with consistent formatting.
 * Errors are dispatched to a pluggable monitoring transport configured via the
 * VITE_MONITORING_ENDPOINT env var (see .env.example). No provider is
 * hardcoded — any endpoint that accepts a POSTed JSON error event works.
 * When the endpoint is not configured (dev/staging) delivery is a no-op, so
 * logging stays dependency-free and can never break the UI.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

/**
 * Payload delivered to the monitoring transport.
 * Stacks are deliberately excluded: they can embed URLs carrying tokens, and
 * the audit contract forbids credentials/PII from leaving the client.
 */
interface MonitoringEvent {
  level: LogLevel;
  context: string;
  message: string;
  errorType?: string;
  extra?: Record<string, unknown>;
}

/**
 * Keys whose values are never allowed to leave the client (auth codes,
 * tokens, cookies, credentials, personal identifiers). Matched
 * case-insensitively against camelCase and snake_case spellings.
 */
const SENSITIVE_KEY_PATTERN =
  /^(?:token|access_token|refresh_token|csrf_token|authcode|challengecode|auth_code|challenge_code|code|authorization|cookie|cookies|set-cookie|password|passwd|secret|api[_-]?key|apikey|credential|credentials|jwt|session|email|phone|mobile)$/i;

/** Redacts credential-style query params inside string values (e.g. URLs). */
const SENSITIVE_PARAM_PATTERN =
  /([?&](?:token|access_token|refresh_token|auth_code|challenge_code|code|signature|key|session|password|secret)=)[^&\s]+/gi;

/** Caps individual string values so large blobs cannot be exfiltrated. */
const MAX_VALUE_LENGTH = 500;

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERN.test(key);
}

function scrubString(value: string): string {
  const scrubbed = value.replace(SENSITIVE_PARAM_PATTERN, '$1[redacted]');
  return scrubbed.length > MAX_VALUE_LENGTH
    ? `${scrubbed.slice(0, MAX_VALUE_LENGTH)}…[truncated]`
    : scrubbed;
}

/**
 * Recursively scrub a context value before it leaves the client:
 * - sensitive keys (auth codes, tokens, cookies, PII) → [redacted]
 * - Error values are reduced to { name, message } — stacks can carry
 *   token-bearing URLs
 * - depth and string length are capped to bound payload size
 */
function sanitizeForMonitoring(value: unknown, depth = 0): unknown {
  if (depth > 3) {
    return '[truncated]';
  }
  if (value instanceof Error) {
    return { name: value.name, message: scrubString(value.message) };
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForMonitoring(item, depth + 1));
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      out[key] = isSensitiveKey(key) ? '[redacted]' : sanitizeForMonitoring(item, depth + 1);
    }
    return out;
  }
  if (typeof value === 'string') {
    return scrubString(value);
  }
  return value;
}

/**
 * Determine if we're in development mode
 */
const isDevelopment = typeof import.meta !== 'undefined' && import.meta.env.MODE === 'development';

/**
 * Format log message with context
 */
function formatMessage(
  level: LogLevel,
  context: string,
  message: string,
  extra?: LogContext
): string {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}] [${context}]`;

  if (extra && Object.keys(extra).length > 0) {
    return `${prefix} ${message} ${JSON.stringify(extra)}`;
  }

  return `${prefix} ${message}`;
}

/**
 * Fire-and-forget delivery to the configured monitoring transport.
 *
 * - Gated on VITE_MONITORING_ENDPOINT being configured: unset → graceful no-op
 *   (dev/staging without config still works)
 * - `credentials: 'omit'` so cookies are never attached to the request
 * - All failures (sync or async) are swallowed — a broken transport must
 *   never surface into the UI
 * - Console suppression (see consoleGuard) does not gate this: delivery goes
 *   straight to fetch, not through console
 */
function sendToMonitoring(
  level: LogLevel,
  context: string,
  message: string,
  error?: unknown,
  extra?: LogContext
): void {
  const endpoint: string | undefined = import.meta.env.VITE_MONITORING_ENDPOINT;
  if (!endpoint) {
    return; // not configured → graceful no-op
  }

  const event: MonitoringEvent = {
    level,
    context,
    message: scrubString(message),
    ...(error !== undefined && {
      errorType: error instanceof Error ? error.name || 'Error' : typeof error,
    }),
    ...(extra !== undefined && {
      extra: sanitizeForMonitoring(extra) as Record<string, unknown>,
    }),
  };

  try {
    void fetch(endpoint, {
      method: 'POST',
      keepalive: true, // let the request survive page unload
      credentials: 'omit', // never send cookies to the monitoring endpoint
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
    }).catch(() => {
      // transport failure swallowed — monitoring must not break the UI
    });
  } catch {
    // synchronous failure (fetch unavailable, invalid endpoint) — same contract
  }
}

/**
 * Logger instance
 */
export const logger = {
  /**
   * Log debug information (development only)
   */
  debug(context: string, message: string, extra?: LogContext): void {
    if (isDevelopment) {
      globalThis.console.debug(formatMessage('debug', context, message, extra));
    }
  },

  /**
   * Log informational messages
   */
  info(context: string, message: string, extra?: LogContext): void {
    if (isDevelopment) {
      globalThis.console.info(formatMessage('info', context, message, extra));
    }
    sendToMonitoring('info', context, message, undefined, extra);
  },

  /**
   * Log warnings
   */
  warn(context: string, message: string, extra?: LogContext): void {
    globalThis.console.warn(formatMessage('warn', context, message, extra));
    sendToMonitoring('warn', context, message, undefined, extra);
  },

  /**
   * Log errors with full context
   */
  error(context: string, error: unknown, extra?: LogContext): void {
    const message = error instanceof Error ? error.message : String(error);
    globalThis.console.error(formatMessage('error', context, message, extra), error);
    sendToMonitoring('error', context, message, error, extra);
  },

  /**
   * Log API errors specifically
   */
  apiError(endpoint: string, error: unknown, extra?: LogContext): void {
    this.error('API', error, { endpoint, ...extra });
  },

  /**
   * Log network errors
   */
  networkError(message: string, error: unknown, extra?: LogContext): void {
    this.error('Network', error, { message, ...extra });
  },

  /**
   * Log authentication errors
   */
  authError(message: string, extra?: LogContext): void {
    this.error('Auth', new Error(message), extra);
  },
};
